import { useMemo, useState } from 'react';
import {
  FieldSelect as LookerSelect,
  type FieldSelectProps as LookerSelectProps,
} from '@looker/components';

import { cls } from '@/utils/cls';

import style from './styles.module.scss';

export type FieldSelectProps = {
  containerClassName?: string;
  options?: { value: string; label?: string }[];
} & Omit<LookerSelectProps, 'onFilter' | 'options'>;

export function FieldSelect({
  containerClassName,
  className,
  options = [],
  ...props
}: FieldSelectProps) {
  const [inputFilter, setInputFilter] = useState('');

  // Normalize options to ensure both value and label are strings
  const normalizedOptions = useMemo(
    function () {
      return options.map((option) => {
        const value = String(option?.value ?? '');
        const label = option?.label != null ? String(option.label) : value;
        return { value, label };
      });
    },
    [options],
  );

  const filteredOptions = useMemo(
    function () {
      if (!props.isFilterable) {
        return normalizedOptions;
      }

      const lowercasedFilter = String(inputFilter ?? '').toLowerCase();
      return normalizedOptions.filter((option) =>
        String(option.label ?? option.value)
          .toLowerCase()
          .includes(lowercasedFilter),
      );
    },
    [inputFilter, normalizedOptions, props.isFilterable],
  );

  return (
    <div className={containerClassName}>
      <LookerSelect
        className={cls(
          style.fieldSelect,
          { [style.value]: props.value },
          className,
        )}
        onFilter={(v) => setInputFilter(v ?? '')}
        options={filteredOptions}
        {...props}
      />
    </div>
  );
}
